import { Flex, VStack, AccordionIcon } from "@chakra-ui/react";
import Tooltip from "components/Tooltip";
import { LengthOfHtmlTextString, truncateHtml } from "helpers/stringHelper";
import SingleAccordion from "newui/components/SingleAccordion";
import SkeletonBase from "newui/components/Skeleton";
import TextBoxHTML from "newui/components/TextBoxHTML";
import { ReactNode, useMemo, useState } from "react";

interface AccordionLargeTextDisplayProps {
	isLoading: boolean;
	maxTextDisplay?: number;
	text: string | null;
	title: string | null;
}

export const AccordionLargeTextDisplay: React.FC<
	AccordionLargeTextDisplayProps
> = ({ isLoading, maxTextDisplay = 150, text, title }) => {
	const [hideAccordionButton, setHideAccordionButton] = useState(false);

	const length = LengthOfHtmlTextString(text);

	const hasText = length > 0;

	const truncatedHtml = useMemo(() => {
		if (!text) {
			return null;
		}
		return truncateHtml(text, { maxCharacters: maxTextDisplay, maxLines: 3 });
	}, [text, maxTextDisplay]);

	if (!hasText) {
		return null;
	}

	if (isLoading) {
		return (
			<SkeletonBase
				width="100%"
				height="48px"
			/>
		);
	}

	const content = (
		title: string,
		displayValue: string,
		isUsingAccordion = true
	): ReactNode => {
		return (
			<>
				<p className="text-left font-roboto font-bold text-gray-text-400">
					{title}
					{isUsingAccordion && <AccordionIcon />}
				</p>
				<TextBoxHTML
					html={displayValue}
					className="text-gray-text-600 text-base font-normal"
				/>
			</>
		);
	};

	if (!truncatedHtml.wasTruncated) {
		return <div className="my-2">{content(title, text ?? "", false)}</div>;
	}

	const getButtonContent = (title: string, displayValue: string): ReactNode => {
		return (
			<div className="pl-1">
				<Tooltip
					label="Click to view all"
					placement="top-start"
				>
					<Flex
						w="full"
						justify="flex-start"
					>
						<VStack align={"start"}>
							{content(title, `${displayValue}<i>click to see all text...</i>`)}
						</VStack>
					</Flex>
				</Tooltip>
			</div>
		);
	};

	const getPanelContent = (
		title: string,
		displayValue: string,
		close: () => void
	) => {
		return (
			<div className="">
				<Tooltip
					label="Click to hide"
					placement="top-start"
				>
					<p
						className="text-gray-text-600 cursor-pointer p-1 pb-2 font-roboto font-bold hover:bg-[#dbeafe]"
						onClick={close}
					>
						{title} <AccordionIcon />
					</p>
				</Tooltip>
				<TextBoxHTML
					html={displayValue}
					className="text-base font-normal text-gray-600"
				/>
			</div>
		);
	};

	const renderAccordionButton = (isOpen: boolean): ReactNode => {
		if (isOpen) {
			return null;
		}

		return getButtonContent(title, truncatedHtml?.value);
	};

	return (
		hasText && (
			<SingleAccordion
				buttonProps={{ style: { textAlign: "left" } }}
				hideButton={hideAccordionButton}
				onAccordionChange={setHideAccordionButton}
				renderButtonContent={renderAccordionButton}
				renderPanelContent={(close, open) =>
					getPanelContent(title, text, close)
				}
			/>
		)
	);
};
